import i18next from "i18next";
import { Constants } from "../models/Constants";
import { StatusCode } from "../models/Enums";
import { BaseMessageModel, ErrorModel } from "../models/ErrorModel";
import { RespondentTypeModel } from "../models/QuestionBank/RespondentTypeModel";
import { RespondentTypeRequestModel } from "../models/QuestionBank/RespondentTypeRequestModel";
import { SubObjectiveIndicatorModel, SurveyQuestionModel } from "../models/QuestionBank/SurveyQuestionModel";
import { getCall, postCall, putCall } from "./api";
import { instance } from "./instance";
import { notificationService } from "./notificationService";
import { SurveyQuestionRequestModel } from "../models/QuestionBank/SurveyQuestionRequestModel";
import { SurveyQuestionFinalizeModel } from "../models/QuestionBank/SurveyQuestionFinalizeModel";
import { DataAnalysisReport } from "../models/ReportModel/ReportModel";

export const questionBankService = {
    /** Gets respondent types selected by specific assessmentId 
     * * @param assessmentId A string value
     */
    getRespondentTypesById: (assessmentId: string) =>
        new Promise<Array<RespondentTypeModel>>((resolve, reject) => {
            // call an api
            const url = Constants.Api.Url.QUESTION_BANK_GET_RESPONDENT_TYPES.replace(
                "{0}",
                assessmentId
            );

            getCall(url).then((response: any) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve([]);
                }
            });
        }),

    /** Saves the respondent types by calling an API
     * request: an object of type RespondentTypeRequestModel
     */
    saveRespondentTypes: (request: RespondentTypeRequestModel) =>
        new Promise<boolean>((resolve, reject) => {
            postCall<RespondentTypeRequestModel>(
                Constants.Api.Url.QUESTION_BANK_SAVE_RESPONDENT_TYPES,
                request
            ).then((response) => {
                resolve(response);
                if (response) {
                    notificationService.sendMessage(
                        new BaseMessageModel(
                            i18next.t("Assessment.DataCollection.QuestionBankSurvey.Message.SaveRespondentTypesSuccessMessage"),
                            StatusCode.Created
                        )
                    );
                } else {
                    notificationService.sendMessage(
                        new BaseMessageModel(
                            i18next.t("Errors.SomethingWentWrong"),
                            StatusCode.PreConditionFailed
                        )
                    );
                }
            })
                .catch((error) => reject(error));
        }),

    /** Gets survey questions by specific assessmentId 
     * * @param assessmentId A string value
     */
    getSurveyQuestionsById: (assessmentId: string) =>
        new Promise<Array<SurveyQuestionModel>>((resolve, reject) => {
            // call an api
            const url = Constants.Api.Url.QUESTION_BANK_GET_SURVEY_QUESTIONS.replace(
                "{0}",
                assessmentId
            );

            getCall(url).then((response: any) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve([]);
                }
            });
        }),

    /** Gets objective, subobjective and indicators for survey questions by specific assessmentId 
     * * @param assessmentId A string value
     */
    getObjectivesSubobjectivesIndicators: (assessmentId: string) =>
        new Promise<SubObjectiveIndicatorModel>((resolve, reject) => {
            // call an api
            const url = Constants.Api.Url.QUESTION_BANK_GET_OBJECTIVE_SUBOBJECTIVE_INDICATORS.replace(
                "{0}",
                assessmentId
            );

            getCall(url).then((response: any) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve(SubObjectiveIndicatorModel.init());
                }
            });
        }),

    /** Saves the survey questions by calling an API
     * request: an object of type SurveyQuestionRequestModel
     */
    saveSurveyQuestions: (request: SurveyQuestionRequestModel) =>
        new Promise<boolean>((resolve, reject) => {
            postCall<SurveyQuestionRequestModel>(
                Constants.Api.Url.QUESTION_BANK_SAVE_SURVEY_QUESTIONS,
                request
            ).then((response) => {
                resolve(response);
                if (response) {
                    notificationService.sendMessage(
                        new BaseMessageModel(
                            i18next.t("Assessment.DataCollection.QuestionBankSurvey.Message.SaveSurveyQuestionSuccessMessage"),
                            StatusCode.Created
                        )
                    );
                } else {
                    notificationService.sendMessage(
                        new BaseMessageModel(
                            i18next.t("Errors.SomethingWentWrong"),
                            StatusCode.PreConditionFailed
                        )
                    );
                }
            })
                .catch((error) => reject(error));
        }),

    /** Finalize survey questions response */
    finalizeSurveyQuestions: (request: SurveyQuestionFinalizeModel) =>
        new Promise((resolve, reject) => {
            putCall<SurveyQuestionFinalizeModel>(
                Constants.Api.Url.QUESTION_BANK_FINALIZE_SURVEY_QUESTIONS,
                request
            )
                .then((response) => {
                    if (response) {
                        notificationService.sendMessage(
                            new BaseMessageModel(
                                i18next.t("Assessment.DataCollection.QuestionBankSurvey.Message.FinalizeSurveyQuestionSuccessMessage"),
                                StatusCode.Created
                            )
                        );
                    } else {
                        notificationService.sendMessage(
                            new BaseMessageModel(
                                i18next.t("Assessment.DataCollection.QuestionBankSurvey.Message.FinalizeSurveyQuestionErrorMessage"),
                                StatusCode.PreConditionFailed
                            )
                        );
                    }
                    resolve(response);
                })
                .catch((error) => reject(error));
        }),

    /** Generate Survey Questionnaire (async background job)
     * @param assessmentId A string value
     * @param onProgress Optional callback called with translated step label on each progress update
     * @param onAllComplete Optional callback called when all steps are complete
     */
    generateSurveyQuestionnaire: async (
        assessmentId: string,
        onProgress?: (step: string) => void,
        onAllComplete?: () => void
    ): Promise<Blob> => {
        onProgress?.(`${i18next.t("Common.Exporting")}...`);

        const startUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.QUESTION_BANK_GENERATE_QUESTIONNAIRE_START.replace("{0}", assessmentId)}`;
        const jobResponse = await instance.post(startUrl, null, { skipGlobalLoader: true } as any);
        const jobId: string = jobResponse?.data?.jobId;
        if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

        const maxAttempts = 900;
        const pollIntervalMs = 2000;
        const stepVisibleMs = 1000;

        const BACKEND_STEPS = ["", "Loading questions...", "Creating ZIP archive..."];
        const stepTranslations: Record<string, string> = {
            "Loading questions...":    i18next.t("Common.ExportStepLoadingQuestions") as string,
            "Creating ZIP archive...": i18next.t("Common.ExportStepCreatingZip") as string,
        };

        let lastShownIndex = 0;
        const animateToStep = async (targetIndex: number) => {
            for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
                onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
                await new Promise(resolve => setTimeout(resolve, stepVisibleMs));
            }
            lastShownIndex = Math.max(lastShownIndex, targetIndex);
        };

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

            const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.QUESTION_BANK_GENERATE_QUESTIONNAIRE_STATUS.replace("{0}", jobId)}`;
            const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
            const { status, currentStep, errorMessage } = statusResponse.data;

            if (currentStep) {
                const stepIndex = BACKEND_STEPS.indexOf(currentStep);
                if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
            }

            if (status === "Succeeded") {
                await animateToStep(BACKEND_STEPS.length - 1);
                onAllComplete?.();
                await new Promise(resolve => setTimeout(resolve, 800));

                const downloadUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.QUESTION_BANK_GENERATE_QUESTIONNAIRE_DOWNLOAD.replace("{0}", jobId)}`;
                const downloadResponse = await instance.get(downloadUrl, { responseType: "blob", skipGlobalLoader: true } as any);
                return downloadResponse.data as Blob;
            }

            if (status === "Failed") {
                const msg = errorMessage || i18next.t("Errors.SomethingWentWrong");
                notificationService.sendMessage(new BaseMessageModel(
                    i18next.t("Errors.SomethingWentWrong"),
                    StatusCode.InternalServerError
                ));
                throw new Error(msg as string);
            }
        }

        throw new Error("Generation timed out after 30 minutes.");
    },

    /** Download the health facilities excel template*/
    downloadHealthFacilities: () =>
        new Promise<any>((resolve, reject) => {
            getCall(Constants.Api.Url.QUESTION_BANK_DOWNLOAD_HEALTH_FACILITIES, "blob").then(
                (response: any) => {
                    resolve(response);
                }
            );
        }),

    /** Upload health facility template — returns 202 Accepted then polls until processing completes.
     *  @param onProgress Optional callback invoked with a human-readable step message at each stage.
     *  @param onAllComplete Optional callback invoked once all steps are visually complete.
     */
    uploadHealthFacilityTemplateResponse: async (
        request: FormData,
        onProgress?: (step: string) => void,
        onAllComplete?: () => void
    ): Promise<boolean> => {
        // Step 1: submit the file; server queues the job and returns 202 { jobId }
        onProgress?.(`${i18next.t("Common.Uploading")}...`);
        const jobResponse: any = await postCall<FormData>(
            Constants.Api.Url.QUESTION_BANK_UPLOAD_HEALTH_FACILITIES,
            request
        );

        const jobId: string = jobResponse?.jobId;
        if (!jobId) {
            throw new Error(i18next.t("Errors.SomethingWentWrong") as string);
        }

        // Step 2: poll status until succeeded / failed (max 10 minutes, 2-second intervals)
        const maxAttempts = 900;
        const pollIntervalMs = 2000;
        const stepVisibleMs = 1000;

        const BACKEND_STEPS = [
            "",
            "Parsing Excel data...",
            "Calculating results...",
            "Saving to database...",
        ];
        const stepTranslations: Record<string, string> = {
            "Parsing Excel data...":  i18next.t("Common.UploadStepParsingExcel") as string,
            "Calculating results...": i18next.t("Common.UploadStepCalculating") as string,
            "Saving to database...":  i18next.t("Common.UploadStepSaving") as string,
        };

        let lastShownIndex = 0;
        const animateToStep = async (targetIndex: number) => {
            for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
                onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
                await new Promise(resolve => setTimeout(resolve, stepVisibleMs));
            }
            lastShownIndex = Math.max(lastShownIndex, targetIndex);
        };

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

            const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.QUESTION_BANK_HEALTH_FACILITY_UPLOAD_STATUS.replace("{0}", jobId)}`;
            const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
            const { status, currentStep, errorMessage } = statusResponse.data;

            if (currentStep) {
                const stepIndex = BACKEND_STEPS.indexOf(currentStep);
                if (stepIndex > lastShownIndex) {
                    await animateToStep(stepIndex);
                }
            }

            if (status === "Succeeded") {
                await animateToStep(BACKEND_STEPS.length - 1);
                onAllComplete?.();
                await new Promise(resolve => setTimeout(resolve, 800));
                notificationService.sendMessage(
                    new BaseMessageModel(
                        i18next.t("Assessment.DataCollection.QuestionBankSurvey.Message.HealthFacilityUploadTemplateSuccessMessage"),
                        StatusCode.Created
                    )
                );
                return true;
            }

            if (status === "Failed") {
                const msg = errorMessage || i18next.t("Errors.SomethingWentWrong");
                notificationService.sendMessage(
                    new BaseMessageModel(
                        `${i18next.t("Assessment.DataCollection.QuestionBankSurvey.Message.HealthFacilityUploadTemplateErrorMessage")}: ${msg}`,
                        StatusCode.PreConditionFailed
                    )
                );
                throw new Error(msg as string);
            }

            // status is Queued or Processing — keep polling
        }

        const timeoutMsg = i18next.t("Errors.SomethingWentWrong") as string;
        notificationService.sendMessage(
            new BaseMessageModel(timeoutMsg, StatusCode.InternalServerError)
        );
        throw new Error("File processing timed out after 30 minutes.");
    },

    /** Async health facility export — returns 202 then polls until the Excel is ready, then downloads it.
     *  @param onProgress Callback invoked with a translated step label at each stage.
     *  @param onAllComplete Callback invoked once all steps are visually complete.
     */
    exportHealthFacilityExcelAsync: async (
        countryId: string,
        onProgress?: (step: string) => void,
        onAllComplete?: () => void
    ): Promise<Blob> => {
        onProgress?.(`${i18next.t("Common.Exporting")}...`);

        const startUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.QUESTION_BANK_EXPORT_HEALTH_FACILITIES_START.replace("{0}", countryId)}`;
        const jobResponse = await instance.post(startUrl, null, { skipGlobalLoader: true } as any);
        const jobId: string = jobResponse?.data?.jobId;
        if (!jobId) throw new Error(i18next.t("Errors.SomethingWentWrong") as string);

        const maxAttempts = 900;
        const pollIntervalMs = 2000;
        const stepVisibleMs = 1000;

        const BACKEND_STEPS = [
            "",
            "Fetching health facility data...",
            "Generating Excel file...",
        ];
        const stepTranslations: Record<string, string> = {
            "Fetching health facility data...": i18next.t("Common.ExportStepFetching") as string,
            "Generating Excel file...":         i18next.t("Common.ExportStepGenerating") as string,
        };

        let lastShownIndex = 0;
        const animateToStep = async (targetIndex: number) => {
            for (let i = lastShownIndex + 1; i <= targetIndex; i++) {
                onProgress?.(stepTranslations[BACKEND_STEPS[i]] ?? BACKEND_STEPS[i]);
                await new Promise(resolve => setTimeout(resolve, stepVisibleMs));
            }
            lastShownIndex = Math.max(lastShownIndex, targetIndex);
        };

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

            const statusUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.QUESTION_BANK_EXPORT_HEALTH_FACILITIES_STATUS.replace("{0}", jobId)}`;
            const statusResponse = await instance.get(statusUrl, { skipGlobalLoader: true } as any);
            const { status, currentStep, errorMessage } = statusResponse.data;

            if (currentStep) {
                const stepIndex = BACKEND_STEPS.indexOf(currentStep);
                if (stepIndex > lastShownIndex) await animateToStep(stepIndex);
            }

            if (status === "Succeeded") {
                await animateToStep(BACKEND_STEPS.length - 1);
                onAllComplete?.();
                await new Promise(resolve => setTimeout(resolve, 800));

                const downloadUrl = `${Constants.Api.baseUrl}/${Constants.Api.Url.QUESTION_BANK_EXPORT_HEALTH_FACILITIES_DOWNLOAD.replace("{0}", jobId)}`;
                const downloadResponse = await instance.get(downloadUrl, { responseType: "blob", skipGlobalLoader: true } as any);
                return downloadResponse.data as Blob;
            }

            if (status === "Failed") {
                const msg = errorMessage || i18next.t("Errors.SomethingWentWrong");
                notificationService.sendMessage(new BaseMessageModel(
                    i18next.t("Errors.SomethingWentWrong"),
                    StatusCode.InternalServerError
                ));
                throw new Error(msg as string);
            }
        }

        throw new Error("Export timed out after 30 minutes.");
    },

    /** Health facility export excel */
    exportHealthFacilityExcel: (countryId: string) =>
        new Promise<boolean>((resolve, reject) => {

            const url = Constants.Api.Url.QUESTION_BANK_EXPORT_HEALTH_FACILITIES.replace(
                "{0}",
                countryId
            );
            getCall(url, "blob").then((response: any) => {
                resolve(response);
            })
                .catch((error: ErrorModel) => {
                    if (error && error.statusCode !== StatusCode.PreConditionFailed) {
                        notificationService.sendMessage(
                            new BaseMessageModel(
                                i18next.t("Errors.SomethingWentWrong"),
                                StatusCode.InternalServerError
                            )
                        );
                    }
                    reject(true);
                });
        }),

    /** Check health facility data
*  @param assessmentId A string value
*/
    hasHealthFacilityData: (assessmentId: string) =>
        new Promise<boolean>((resolve, reject) => {
            // call an api
            const url = Constants.Api.Url.QUESTION_BANK_HAS_HEALTH_FACILITIES_DATA.replace(
                "{0}",
                assessmentId
            );

            getCall(url).then((response: any) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve(false);
                }
            });
        }),

    /** Health facility export excel for assessment */
    exportHealthFacilityExcelForAssessment: (assessmentId: string, countryId: string) =>
        new Promise<boolean>((resolve, reject) => {
            const url = Constants.Api.Url.QUESTION_BANK_EXPORT_HEALTH_FACILITIES_FOR_ASSESSMENT
                .replace("{0}", assessmentId)
                .replace("{1}", countryId);

            getCall(url, "blob").then((response: any) => {
                resolve(response);
            })
                .catch((error: ErrorModel) => {
                    if (error) {
                        notificationService.sendMessage(
                            new BaseMessageModel(
                                i18next.t("Errors.SomethingWentWrong"),
                                StatusCode.PreConditionFailed
                            )
                        );
                    }
                    reject(true);
                });
        }),

    /** Gets Last Uploaded Health Facility file name  by specific CountryId  
    * * @param CountryId A string value
    */
    getLastUploadedFileName: (countryId: string) =>
        new Promise<string>((resolve, reject) => {
            // call an api
            const url = Constants.Api.Url.QUESTION_BANK_GET_UPLOADES_FILENAME.replace(
                "{0}",
                countryId
            );

            getCall(url).then((response: any) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve("");
                }
            });
        }),


    /** Gets Last Uploaded Health Facility file name  by specific CountryId  
        * * @param CountryId A string value
        */
    getUploadedShellReports: (assessmentId: string) =>
        new Promise<DataAnalysisReport>((resolve, reject) => {
            // call an api
            const url = Constants.Api.Url.QUESTION_BANK_GET_UPLOADED_SHELL_REPORT_FILES.replace(
                "{0}",
                assessmentId
            );

            getCall(url).then((response: any) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve(DataAnalysisReport.init());
                }
            });
        }),

}
